import { AnyAction, Dispatch, Middleware } from "redux";

import getFloorId from "../getters/floor_getters/get_floor_id";
import getFloorNumber from "../getters/floor_getters/get_floor_number";
import getPlannedElementsByGlobalId from "../getters/planned_building_element_getters/get_planned_elements_by_global_id";
import getProject from "../getters/project_getters/get_project";
import getProjectId from "../getters/project_getters/get_project_id";
import getScanDatasetId from "../getters/scan_dataset_getters/get_scan_dataset_id";
import PlannedBuildingElement from "../../models/domain/planned_building_element";
import realMixpanel, { Mixpanel } from "./init_mixpanel";
import { ACCEPT_INVITATION_STARTED } from "../../events/auth_events/accept_invitation_started";
import { AvvirEvent } from "type_aliases";
import { ELEMENT_FOCUSED } from "../../events/viewer/element_focused";
import { ELEMENT_SELECTED } from "../../events/viewer/element_selected";
import { EXPORT_IFC_TRIGGERED } from "../../events/export_ifc_triggered";
import { INSPECTION_MODE_SELECTED } from "../../events/selection/inspection_mode_selected";
import { isRoutingEvent } from "../routing_events";
import { ReduxStore } from "../reducers/root_reducer";
import { SIGN_IN_USER_STARTED } from "../../events/auth_events/sign_in_user_started";
import { TO_PROJECT } from "../../events/routing/to_project";
import { TO_VIEWER_SCAN_DATASET } from "../../events/routing/to_viewer_scan_dataset";
import amplitudeInstance, { initiateAmplitudeTracking, resetAmplitudeIdentity } from "./init_amplitude";
import { sanitizeEventPayload } from "./event_payload_sanitizer";
import { LDFlagSet } from "launchdarkly-react-client-sdk";
import { CUSTOM_SUCCESS_MESSAGE_DISPLAYED } from "../../events/notifications/success_notifications/custom_success_message_displayed";
import { FIREBASE_USER_SIGNED_IN } from "../../events/auth_events/firebase_user_signed_in";
import { FIREBASE_SIGNED_IN_WITH_CUSTOM_TOKEN } from "../../events/auth_events/firebase_signed_in_with_custom_token";
import { BUILT_TO_NOT_BUILT_CONFIRMATION_DIALOG_SHOWN } from "../../events/viewer/built_to_not_built_confirmation_dialog_shown";
import { BIM_AND_PHOTO_CAMERA_LINKED } from "../../events/viewer/bim_and_photo_camera_linked";
import { ELEMENTS_PARTIAL_PROGRESS_UPDATED } from "../../events/viewer/elements_partial_progress_updated";
import { SET_ANNOTATION_INTEGRATION_ISSUES } from "../../actions/integrations/load_all_integration_issues";
import { FLOOR_PLAN_SHOWN } from "../../events/selection/floor_plan_shown";
import { TO_INSPECT_REPORT } from "../../events/routing/to_inspect_report";
import { TO_INTEGRATION_PAGE } from "../../events/routing/to_integration_page";
import { TO_ACCEPT_INVITATION_PAGE } from "../../events/routing/to_accept_invitation_page";
import { TO_CONFIRM_ACCOUNT_PAGE } from "../../events/routing/to_confirm_account_page";
import { TO_CUSTOM_REPORT } from "../../events/routing/to_custom_report";
import { TO_CUSTOM_REPORTS } from "../../events/routing/to_custom_reports";
import { TO_HX_AUTH_LOGIN_PAGE } from "../../events/routing/to_hx_auth_login_page";
import { TO_LOGIN_PAGE } from "../../events/routing/to_login_page";
import { TO_PASSWORD_RESET_CONFIRMATION_PAGE } from "../../events/routing/to_password_reset_confirmation_page";
import { TO_PASSWORD_RESET_PAGE } from "../../events/routing/to_password_reset_page";
import { TO_PASSWORD_RESET_EMAIL_FORM } from "../../events/routing/to_password_reset_email_form";
import { TO_PHOTO_PAGE } from "../../events/routing/to_photo_page";
import { TO_PROJECT_INTEGRATIONS_PAGE } from "../../events/routing/to_project_integrations_page";
import { TO_PROJECT_PORTFOLIO_PAGE } from "../../events/routing/to_project_portfolio_page";
import { TO_PROJECT_SETTINGS_PAGE } from "../../events/routing/to_project_settings_page";
import { TO_PROJECT_SUMMARY_PAGE } from "../../events/routing/to_project_summary_page";
import { TO_PROJECT_UPLOADS_PAGE } from "../../events/routing/to_project_uploads_page";
import { TO_PROJECT_TEAM_PAGE } from "../../events/routing/to_project_team_page";
import { TO_SEARCH_SET_CREATOR_PAGE } from "../../events/routing/to_search_set_creator_page";
import { PUSHED_TO_BIM } from "../../events/viewer/pushed_to_bim";
import { PUSHED_TO_BIMTRACK } from "../../events/viewer/pushed_to_bimtrack";
import { PUSHED_ALL_TO_BIM } from "../../events/viewer/pushed_all_to_bim";
import { HXAUTH_USER_SIGNED_IN } from "../../events/auth_events/hxauth_user_signed_in";
import { PHOTO_LOCATION_SELECTED } from "../../events/selection/photos/photo_location_selected";
import { PHOTO_LOCATIONS_DELETED } from "../../events/viewer/photo_locations_deleted";
import { GATEWAY_USER_SIGNED_IN } from "../../events/auth_events/gateway_user_signed_in";
import { GROUP_CREATED } from "../../events/groups/group_created";
import { GROUP_DELETED } from "../../events/groups/group_deleted";
import { GROUP_UPDATED } from "../../events/groups/group_updated";
import { GROUP_MEMBERS_CREATED } from "../../events/groups/group_members_created";
import { GROUP_MEMBERS_DELETED } from "../../events/groups/group_members_deleted";
import { FILE_UPLOAD_STARTED } from "../../events/files/upload/file_upload_started";
import { FLOOR_FILE_UPLOADED } from "../../events/files/upload/floor_file_uploaded";
import { FILE_ASSOCIATIONS_UPDATED_RETAIN_FLOOR_FILES } from "../../events/files/file_associtions_updated_retain_floor_files";
import { PROJECT_FILE_UPLOADED } from "../../events/files/upload/project_file_uploaded";
import { PHOTO_AREA_FILE_UPLOADED } from "../../events/files/upload/photo_area_file_uploaded";
import { SCAN_DATASET_DELETED } from "../../events/superadmin/scan_datasets/scan_dataset_deleted";
import { SCAN_DATASET_UPDATED } from "../../events/superadmin/scan_datasets/scan_dataset_updated";
import { PHOTO_LOCATIONS_CREATED } from "../../events/photos/photo_locations_created";
import { PHOTO_LOCATIONS_MOVED } from "../../events/viewer/photo_locations_moved";
import { PANELS_RESET } from "../../events/selection/panels/panels_reset";
import { CREATED_JIRA_TICKET_SUCCESSFULLY } from "../../events/superadmin/projects/jira_service_ticket_created";
import { CREATED_JIRA_ACS_WORK_ITEM_SUCCESSFULLY } from "../../events/superadmin/projects/jira_acs_work_item_created";
import { DEVIATION_EDITING_MODE_TOGGLED } from "../../events/selection/deviation_editing_mode_toggled";
import { DEVIATION_MARKED_AS_NOT_RESOLVED } from "../../events/viewer/deviation_marked_as_not_resolved";
import { DEVIATION_MARKED_AS_RESOLVED } from "../../events/viewer/deviation_marked_as_resolved";
import { BIM_REVERT_SUCCEEDED } from "../../events/notifications/success_notifications/bim_revert_succeeded";
import { FILE_DELETED } from "../../events/files/file_deleted";
import { PHOTO_SESSIONS_DELETED } from "../../events/viewer/photo_sessions_deleted";
import { PROJECT_UNARCHIVED } from "../../events/superadmin/projects/project_unarchived";
import { PROJECT_ARCHIVED } from "../../events/superadmin/projects/project_archived";
import { PROJECT_AREA_CREATED } from "../../events/project_summary/project_area_created";
import { PROJECT_AREA_UPDATED } from "../../events/project_summary/project_area_updated";
import { AUTODESK_ISSUE_CREATION_DIALOG_SHOWN } from "../../events/viewer/autodesk_issue_creation_dialog_shown";
import { PUSH_TO_PROCORE_DIALOG_SHOWN } from "../../events/viewer/push_to_procore_dialog_shown";
import { BIMTRACK_ISSUE_CREATION_DIALOG_SHOWN } from "../../events/viewer/bimtrack_issue_creation_dialog_shown";
import { EXPORTED_TO_BCF } from "../../events/viewer/exported_to_bcf";
import { REVIEW_MODE_TOGGLED_ON } from "../../events/viewer/review_mode_toggled_on";
import { REVIEW_MODE_TOGGLED_OFF } from "../../events/viewer/review_mode_toggled_off";
import { TOLERANCE_FILTER_CHANGED } from "../../events/viewer/tolerance_filter_changed";
import { BIM_AND_PHOTO_CAMERA_UNLINKED } from "../../events/viewer/bim_and_photo_camera_unlinked";
import { FLOOR_LOADED } from "../../events/loaded/floors/floor_loaded";
import { VIEWER_RENDER_COMPLETED } from "../../events/viewer/viewer_render_completed";
import { PUSH_TO_BIM_UNDONE } from "../../events/viewer/push_to_bim_undone";
import { ELEMENTS_STATUSES_UPDATED } from "avvir";
import { AUTO_VERIFY_RAN } from "../../events/auto_verify_ran";
import { OBSTRUCTIONS_DELETED } from "../../events/viewer/obstructions_deleted";
import { OBSTRUCTIONS_CREATED } from "../../events/viewer/obstructions_created";
import { COMMENT_THREAD_CREATED } from "../../events/comments/comment_thread_created";
import { COMMENT_CREATED } from "../../events/comments/comment_created";
import { INSPECT_REPORT_CREATED } from "../../events/loaded/projects/inspect_report_created";
import { INSPECT_REPORT_ENTRIES_CREATED } from "../../events/loaded/projects/inspect_report_entries_created";
import { INSPECT_REPORT_DELETED } from "../../events/loaded/projects/inspect_report_deleted";
import { INSPECT_REPORT_UPDATED } from "../../events/loaded/projects/inspect_report_updated";
import { INSPECT_REPORT_ENTRY_DELETED } from "../../events/loaded/projects/inspect_report_entry_deleted";
import { INSPECT_REPORT_ENTRY_UPDATED } from "../../events/loaded/projects/inspect_report_entry_updated";
import { DEFAULT_SCAN_DATASET_FOR_FLOOR_UPDATED } from "../../events/superadmin/scan_datasets/default_scan_dataset_for_floor_updated";
import { SCAN_DATASET_FILE_UPLOADED } from "../../events/files/upload/scan_dataset_file_uploaded";
import { PROJECT_AREA_WORK_PACKAGES_UPDATED } from "../../events/project_summary/project_area_work_packages_updated";
import { REPORTED_QUANTITY_UPDATED } from "../../events/reported_quantity_updated";
import { PROJECT_COST_ANALYSIS_PROGRESS_REPORTED_QUANTITY_CHANGED } from "../../events/project_cost_analysis_progress_reported_quantity_changed";
import { PROJECT_RECIPE_CREATED } from "../../events/recipes/project_recipe_created";
import { PROJECT_RECIPES_UPDATED } from "../../events/recipes/project_recipes_updated";
import { PROJECT_RECIPE_REMOVED } from "../../events/recipes/project_recipe_removed";
import { ORGANIZATION_MEMBER_UPDATED } from "../../events/superadmin/organization_member_updated";
import { ORGANIZATION_MEMBER_CREATED } from "../../events/superadmin/organization_member_created";
import { FLOOR_UPDATED } from "../../events/superadmin/floors/floor_updated";
import { ZIP_PROJECT_FOLDER_STARTED } from "../../events/zip_project_folder_started";
import { USER_SIGNED_OUT } from "../../events/auth_events/user_signed_out";
import { TEAM_MEMBERS_CREATED } from "../../events/project_settings/team_members_created";
import { TEAM_MEMBERS_UPDATED } from "../../events/project_settings/team_members_updated";
import { INTEGRATION_CREDENTIALS_SAVED } from "../../events/integration_credentials/integration_credentials_saved";
import { PROJECT_REPORT_REMOVED } from "../../events/superadmin/projects/project_report_removed";
import { FLOOR_DELETED } from "../../events/superadmin/floors/floor_deleted";
import { USER_ACCOUNT_UPDATED } from "../../events/auth_events/user_account_updated";
import { WORK_PACKAGE_CREATED } from "../../events/project_settings/work_package_created";
import { WORK_PACKAGE_UPDATED } from "../../events/project_settings/work_package_updated";
import { WORK_PACKAGE_DELETED } from "../../events/project_settings/work_package_deleted";
import { BIMTRACK_ASSOCIATION_CREATED } from "../../events/viewer/bimtrack_association_created";
import { FLOOR_CREATED } from "../../events/superadmin/floors/floor_created";
import { INVITATION_SENT } from "../../events/notifications/success_notifications/invitation_sent";
import { USER_PERMISSION_CREATED } from "../../events/superadmin/user_permission_created";
import { USER_PERMISSION_DELETED } from "../../events/superadmin/user_permission_deleted";
import { USER_INVITATION_GENERATED } from "../../events/superadmin/invitations/user_invitation_generated";
import { TO_PROGRESS_4D_REPORT } from "../../events/routing/to_progress_4d_report";
import { TO_PROGRESS_4D_REPORT_FOR_AREA } from "../../events/routing/to_progress_4d_report_for_area";
import { TO_SUMMARY_PROGRESS_4D_REPORT } from "../../events/routing/to_summary_progress_4d_report";
import { TO_SUPERADMIN_ORGANIZATION } from "../../events/routing/superadmin/to_superadmin_organization";
import { CAPTURED_TRADE_COSTS_MATERIALIZED_VIEW_REFRESHED } from "../../events/trades/captured_trade_costs_materialized_view_refreshed";
import { PROJECT_UPDATED } from "../../events/superadmin/projects/project_updated";
import { INTEGRATION_PUSH_CLICKED } from "../../events/viewer/integration_push_clicked";
import { ADD_TO_REPORT_CLICKED } from "../../events/viewer/add_to_report_clicked";
import { PUSHED_TO_PROCORE } from "../../events/viewer/pushed_to_procore";
import { PAGE_VIEW_ENDED } from "../../events/page_view_ended";
import { TO_INSPECT_REPORTS } from "../../events/routing/to_inspect_reports";

let mixpanel = realMixpanel;

export function mockMixpanel(mockedMixpanel: Mixpanel) {
  mixpanel = mockedMixpanel;
}

export function unmockMixpanel() {
  mixpanel = realMixpanel;
}

export const eventAliases = {
  "export_pushed_to_bim_ifc_started": "IFC Exported",
  [ELEMENT_FOCUSED]: "Element Zoomed To",
  [TO_PROJECT]: "Viewer Page Visited",
  "push_pdf_to_procore_started": "Pdf Pushed To Procore",
  [ACCEPT_INVITATION_STARTED]: "Attempted New User Creation",
};

function titleCaseWord(word: string): string {
  return word.charAt(0).toUpperCase() + word.slice(1);
}

function friendlyName(eventType: string): string {
  if (eventAliases[eventType]) {
    return eventAliases[eventType];
  } else {
    return eventType.split("_").map(titleCaseWord).join(" ");
  }
}

const authEvents = [
  FIREBASE_USER_SIGNED_IN,
  FIREBASE_SIGNED_IN_WITH_CUSTOM_TOKEN,
  HXAUTH_USER_SIGNED_IN,
  GATEWAY_USER_SIGNED_IN,
  USER_SIGNED_OUT,
  "start_session",
];

const userAccountEvents = [
  USER_ACCOUNT_UPDATED,
  USER_PERMISSION_CREATED,
  USER_PERMISSION_DELETED,
  USER_INVITATION_GENERATED,
  ACCEPT_INVITATION_STARTED,
  INVITATION_SENT,
  "send_password_reset_email_done",
  "reset_password_done",
  "saved_successfully",
];

const orgAndTeamEvents = [
  ORGANIZATION_MEMBER_CREATED,
  ORGANIZATION_MEMBER_UPDATED,
  TEAM_MEMBERS_CREATED,
  TEAM_MEMBERS_UPDATED,
  "generate_project_team_invitations_done",
  "update_existing_organization_member_done",
  "remove_organization_member_done",
  "create_organization_done",
];

const navigationEvents = [
  TO_PROJECT,
  TO_PROJECT_SUMMARY_PAGE,
  TO_PROJECT_SETTINGS_PAGE,
  TO_PROJECT_UPLOADS_PAGE,
  TO_PROJECT_TEAM_PAGE,
  TO_PROJECT_INTEGRATIONS_PAGE,
  TO_PROJECT_PORTFOLIO_PAGE,
  TO_PROGRESS_4D_REPORT,
  TO_PROGRESS_4D_REPORT_FOR_AREA,
  TO_SUMMARY_PROGRESS_4D_REPORT,
  TO_CUSTOM_REPORT,
  TO_CUSTOM_REPORTS,
  TO_INTEGRATION_PAGE,
  TO_PHOTO_PAGE,
  TO_INSPECT_REPORT,
  TO_INSPECT_REPORTS,
  TO_SEARCH_SET_CREATOR_PAGE,
  TO_SUPERADMIN_ORGANIZATION,
  TO_ACCEPT_INVITATION_PAGE,
  TO_CONFIRM_ACCOUNT_PAGE,
  TO_LOGIN_PAGE,
  TO_HX_AUTH_LOGIN_PAGE,
  TO_PASSWORD_RESET_PAGE,
  TO_PASSWORD_RESET_EMAIL_FORM,
  TO_PASSWORD_RESET_CONFIRMATION_PAGE,
  "page_viewed",
];

const projectEvents = [
  PROJECT_UPDATED,
  PROJECT_ARCHIVED,
  PROJECT_UNARCHIVED,
  PROJECT_AREA_CREATED,
  PROJECT_AREA_UPDATED,
  PROJECT_AREA_WORK_PACKAGES_UPDATED,
  PROJECT_COST_ANALYSIS_PROGRESS_REPORTED_QUANTITY_CHANGED,
  PROJECT_RECIPE_CREATED,
  PROJECT_RECIPES_UPDATED,
  PROJECT_RECIPE_REMOVED,
  PROJECT_REPORT_REMOVED,
  PROJECT_FILE_UPLOADED,
  "create_project_done",
];

const floorEvents = [
  FLOOR_CREATED,
  FLOOR_DELETED,
  FLOOR_UPDATED,
  FLOOR_FILE_UPLOADED,
];

const workPackageEvents = [
  WORK_PACKAGE_CREATED,
  WORK_PACKAGE_UPDATED,
  WORK_PACKAGE_DELETED,
];

const groupEvents = [
  GROUP_CREATED,
  GROUP_DELETED,
  GROUP_UPDATED,
  GROUP_MEMBERS_CREATED,
  GROUP_MEMBERS_DELETED,
];

const scanDatasetEvents = [
  SCAN_DATASET_UPDATED,
  SCAN_DATASET_DELETED,
  DEFAULT_SCAN_DATASET_FOR_FLOOR_UPDATED,
  SCAN_DATASET_FILE_UPLOADED,
];

const photoEvents = [
  PHOTO_LOCATION_SELECTED,
  PHOTO_LOCATIONS_CREATED,
  PHOTO_LOCATIONS_MOVED,
  PHOTO_LOCATIONS_DELETED,
  PHOTO_SESSIONS_DELETED,
];

const fileEvents = [
  FILE_UPLOAD_STARTED,
  PHOTO_AREA_FILE_UPLOADED,
  FILE_ASSOCIATIONS_UPDATED_RETAIN_FLOOR_FILES,
  FILE_DELETED,
  ZIP_PROJECT_FOLDER_STARTED,
  "download_filtered_5d_tsv",
  "export_trade_costs_table_tsv",
];

const inspectReportEvents = [
  ADD_TO_REPORT_CLICKED,
  INSPECT_REPORT_CREATED,
  INSPECT_REPORT_ENTRIES_CREATED,
  INSPECT_REPORT_UPDATED,
  INSPECT_REPORT_DELETED,
  INSPECT_REPORT_ENTRY_UPDATED,
  INSPECT_REPORT_ENTRY_DELETED,
];

const commentEvents = [
  COMMENT_THREAD_CREATED,
  COMMENT_CREATED,
];

const bimAndDeviationEvents = [
  PUSHED_TO_BIM,
  PUSHED_ALL_TO_BIM,
  PUSH_TO_BIM_UNDONE,
  BIM_REVERT_SUCCEEDED,
  BIM_AND_PHOTO_CAMERA_LINKED,
  BIM_AND_PHOTO_CAMERA_UNLINKED,
  OBSTRUCTIONS_CREATED,
  OBSTRUCTIONS_DELETED,
  DEVIATION_MARKED_AS_RESOLVED,
  DEVIATION_MARKED_AS_NOT_RESOLVED,
  DEVIATION_EDITING_MODE_TOGGLED,
  ELEMENTS_STATUSES_UPDATED,
  ELEMENTS_PARTIAL_PROGRESS_UPDATED,
  AUTO_VERIFY_RAN,
  REPORTED_QUANTITY_UPDATED,
  "elements_verified_updated",
];

const integrationEvents = [
  INTEGRATION_PUSH_CLICKED,
  PUSHED_TO_PROCORE,
  INTEGRATION_CREDENTIALS_SAVED,
  PUSHED_TO_BIMTRACK,
  BIMTRACK_ASSOCIATION_CREATED,
  BIMTRACK_ISSUE_CREATION_DIALOG_SHOWN,
  PUSH_TO_PROCORE_DIALOG_SHOWN,
  EXPORTED_TO_BCF,
  SET_ANNOTATION_INTEGRATION_ISSUES,
  CREATED_JIRA_TICKET_SUCCESSFULLY,
  CREATED_JIRA_ACS_WORK_ITEM_SUCCESSFULLY,
  AUTODESK_ISSUE_CREATION_DIALOG_SHOWN,
  "create_revizto_issue_done",
  "create_autodesk_request_done",
  "connect_external_photo_service_done",
  "sync_integration_photo_areas_done",
  "export_batch_ifc_done",
  "export_pushed_to_bim_ifc_started",
  "push_pdf_to_procore_started",
];

const reportEvents = [
  CAPTURED_TRADE_COSTS_MATERIALIZED_VIEW_REFRESHED,
];

const viewerAndUiEvents = [
  VIEWER_RENDER_COMPLETED,
  ELEMENT_FOCUSED,
  REVIEW_MODE_TOGGLED_ON,
  REVIEW_MODE_TOGGLED_OFF,
  FLOOR_PLAN_SHOWN,
  BUILT_TO_NOT_BUILT_CONFIRMATION_DIALOG_SHOWN,
  TOLERANCE_FILTER_CHANGED,
  PANELS_RESET,
  CUSTOM_SUCCESS_MESSAGE_DISPLAYED,
  "create_view_done",
];

const pageAnalyticsEvents = [
  PAGE_VIEW_ENDED,
];

export const trackedEvents = new Set<string>([
  ...authEvents,
  ...userAccountEvents,
  ...orgAndTeamEvents,
  ...navigationEvents,
  ...projectEvents,
  ...floorEvents,
  ...workPackageEvents,
  ...groupEvents,
  ...scanDatasetEvents,
  ...photoEvents,
  ...fileEvents,
  ...inspectReportEvents,
  ...commentEvents,
  ...bimAndDeviationEvents,
  ...integrationEvents,
  ...reportEvents,
  ...viewerAndUiEvents,
  ...pageAnalyticsEvents,
]);

const untrackedEvents = new Set([
  FLOOR_LOADED,
]);

let lastTrackedEventDetails: string | null = null;

export function resetTrackedEventTrackingDedupeState() {
  lastTrackedEventDetails = null;
}

export function resetRoutingEventTrackingDedupeState() {
  resetTrackedEventTrackingDedupeState();
}

function serializeValue(value: unknown): string {
  if (!value) {
    return "null";
  }

  if (Array.isArray(value)) {
    return `[${value.map(serializeValue).join(",")}]`;
  }

  if (typeof value === "object") {
    return `{${Object.keys(value).sort().map((key) => `${key}:${serializeValue(value[key])}`).join(",")}}`;
  }

  return JSON.stringify(value);
}

function buildTrackedEventTrackingDetails(event: AnyAction, userEmail: string | null): string {
  return serializeValue({
    type: event.type,
    payload: event.payload ?? null,
    userEmail,
  });
}

// Intentional, legitimately-repeatable click events bypass the consecutive-duplicate guard, so e.g.
// clicking the same button twice in a row is counted twice (Dashboards 1 & 2 are raw counts).
const alwaysForwardEventTypes = new Set<string>([
  INTEGRATION_PUSH_CLICKED,
  ADD_TO_REPORT_CLICKED,
]);

function shouldSkipTrackingDuplicateTrackedEvent(event: AnyAction, userEmail: string | null): boolean {
  const trackingDetails = buildTrackedEventTrackingDetails(event, userEmail);
  const isDuplicate = trackingDetails === lastTrackedEventDetails;

  if (!isDuplicate) {
    lastTrackedEventDetails = trackingDetails;
  }

  return isDuplicate;
}

function shouldTrackEvent(event: AnyAction) {
  return !!event.type && trackedEvents.has(event.type) && !untrackedEvents.has(event.type);
}

function getProjectIdFromEvent(event: AvvirEvent<any, any>, state: ReduxStore): string | null {
  if (event.payload?.projectId) {
    return event.payload.projectId;
  } else {
    return getProjectId(state, {});
  }
}

function getFloorIdFromEvent(event: AvvirEvent<any, any>, state: ReduxStore): string | null {
  if (event.payload?.floorId) {
    return event.payload.floorId;
  } else {
    return getFloorId(state, {});
  }
}

function getScanDatasetIdFromEvent(event: AvvirEvent<any, any>, state: ReduxStore): string | null {
  if (event.payload?.scanDatasetId) {
    return event.payload.scanDatasetId;
  } else {
    return getScanDatasetId(state, {});
  }
}

function getFloorNumberFromEvent(event: AvvirEvent<any, any>, state: ReduxStore): string | null {
  if (event.payload?.floorNumber) {
    return event.payload.floorNumber;
  } else if (event.payload?.floorId) {
    return getFloorNumber(state, { floorId: event.payload.floorId });
  } else {
    return getFloorNumber(state, {});
  }
}

function getScanNumberFromEvent(event: AvvirEvent<any, any>, state: ReduxStore): number {
  if (event.payload?.scanNumber) {
    return event.payload.scanNumber;
  } else {
    return state.domain.scanDatasets?.byFirebaseId?.[getScanDatasetIdFromEvent(event, state)]?.scanNumber;
  }
}

function getScanDateFromEvent(event: AvvirEvent<any, any>, state: ReduxStore): Date {
  if (event.payload?.scanDate) {
    return event.payload.scanDate;
  } else {
    return state.domain.scanDatasets?.byFirebaseId?.[getScanDatasetIdFromEvent(event, state)]?.scanDate;
  }
}

let ldClient = null;

function getLaunchDarklyAllFlags(): LDFlagSet {
  ldClient = window.Avvir?.featureFlags?.ldClient;
  return ldClient?.allFlags();
}

const eventTrackingMiddleware: Middleware<object, ReduxStore> = store => (next: Dispatch) => async event => {
  const result = next(event);

  if (shouldTrackEvent(event)) {
    const state = store.getState();

    const trackInMixpanel = getLaunchDarklyAllFlags()?.trackInMixpanel;
    const userEmail = state?.user?.account?.email ?? null;

    const shouldTrackInAmplitude = trackInMixpanel === false && userEmail !== null;

    if (shouldTrackInAmplitude) {
      initiateAmplitudeTracking(userEmail);
    }

    let eventName = friendlyName(event.type);
    try {
      const isConsecutiveDuplicate = shouldSkipTrackingDuplicateTrackedEvent(event, userEmail);
      if (isConsecutiveDuplicate && !alwaysForwardEventTypes.has(event.type)) {
        return result;
      }

      const projectId = getProjectIdFromEvent(event, state);
      const projectName = getProject(state, {})?.name;

      let floorId, floorNumber, scanDatasetId, scanNumber, scanDate;
      if (!isRoutingEvent(event)) {
        floorId = getFloorIdFromEvent(event, state);
        floorNumber = getFloorNumberFromEvent(event, state);
        scanDatasetId = getScanDatasetIdFromEvent(event, state);
        scanNumber = getScanNumberFromEvent(event, state);
        scanDate = getScanDateFromEvent(event, state);
      }

      if (event.type === TO_VIEWER_SCAN_DATASET && event.payload?.query?.photoLocationId) {
        eventName = "To Photo Location";
      }

      const defaultEventProperties = {
        floorId,
        floorNumber,
        locationProjectId: state.locationMetadata.projectId,
        pageName: state.locationMetadata.pageName,
        projectId,
        projectName,
        reduxEventType: event.type,
        scanDatasetId,
        scanNumber
      };

      let eventProperties;
      if (event.type === ELEMENT_SELECTED || event.type === ELEMENT_FOCUSED) {
        const elementsByGlobalId = getPlannedElementsByGlobalId(state, {}) || {};
        const selectedElement = elementsByGlobalId[event.payload] as PlannedBuildingElement || {} as PlannedBuildingElement;
        const selectedElementName = selectedElement.name;
        const selectedElementDiscipline = selectedElement.discipline;
        const selectedElementUniformat = selectedElement.uniformat;
        const selectedElementIfcType = selectedElement.ifcType;
        const built = PlannedBuildingElement.isBuilt(selectedElement, scanDate);
        let deviationInfo;
        if (PlannedBuildingElement.isDeviated(selectedElement, scanDate)) {
          deviationInfo = {
            deviationMagnitude: PlannedBuildingElement.getDeviation(selectedElement, scanDate).deviationMeters,
            status: PlannedBuildingElement.getDeviation(selectedElement, scanDate).status
          }
        }

        eventProperties = {
          ...deviationInfo,
          built,
          selectedElementGlobalId: event.payload,
          selectedElementName,
          selectedElementDiscipline,
          selectedElementUniformat,
          selectedElementIfcType
        };
      } else if (event.type === ACCEPT_INVITATION_STARTED || event.type === SIGN_IN_USER_STARTED) {
        eventProperties = sanitizeEventPayload(event.payload);
      } else if (event.type === INSPECTION_MODE_SELECTED) {
        eventProperties = { inspectionMode: event.payload };
      } else if (event.type === PHOTO_LOCATION_SELECTED) {
        eventProperties = {
          photoLocationId: event.payload.photoLocationId,
          fenceSelectedPhotoLocationIds: event.payload.fenceSelectedPhotoLocationIds ?? null,
        };
      } else if (event.type === VIEWER_RENDER_COMPLETED) {
        eventProperties = event.payload;
      } else if (typeof event.payload === "string") {
        eventProperties = { payload: event.payload };
      } else if ((event.type.endsWith("_failed") || event.type.endsWith("_failure")) && event.payload) {
        eventProperties = {
          errorType: event.payload.name,
          errorMessage: event.payload.message,
          errorStacktrace: event.payload.stack,
        };

      } else if (event.type === EXPORT_IFC_TRIGGERED){
        eventProperties = {
          ifcType: event.payload.ifcType,
          startTime: event.payload.startTime,
          processId: event.payload.processId
        }
      } else {
        eventProperties = sanitizeEventPayload(event.payload);
      }
      // WARNING: be mindful of how large the payload you send to amplitude is.
      // It can slow down user experience if you e.g. accidentally send up a pointcloud,
      // or fail altogether if the object has cycles and can't serialize

      // @ts-ignore
      if (shouldTrackInAmplitude) {
        amplitudeInstance?.track(eventName, {
          ...eventProperties,
          ...defaultEventProperties
        });
      } else if (trackInMixpanel) {
        mixpanel.track(eventName, {
          ...eventProperties,
          ...defaultEventProperties
        });
      }
    } catch (err) {
      console.error(err);

      // @ts-ignore
      if (shouldTrackInAmplitude) {
        amplitudeInstance.track(`tracking failed for ${eventName}`);
      } else if (trackInMixpanel) {
        mixpanel.track(`tracking failed for ${eventName}`);
      }
    }
  }
  if (event.type === USER_SIGNED_OUT) {
    resetAmplitudeIdentity();
  }

  return result;
};

export default eventTrackingMiddleware;
